import React from 'react';
import { Col, Flex, message, Row, Typography } from 'antd';
import { Heading } from '../Heading';
import { CopyIcon, UrlIcon } from '../Icons';
import { Card } from '../Card';
import { useTranslation } from 'react-i18next';
import { StatusTag } from '../Status';
import { getOrderStatusTagData } from '@/utils';
import { OrderStatusEnum } from '@/config';
import { ShipmentStatus } from '@/types';

const { Text } = Typography;

// Define colors for each status

// Define the Props interface
interface OrderInfoCardProps {
  title: string;
  orderIdLabel: string;
  orderId?: string;
  estimateDeliveryLabel?: string;
  estimateDelivery?: string;
  trackingUrlLabel: string;
  trackingUrl: string;
  trackingAuth?: string;
  clueVerificationLink?: string;
  idCheck?: boolean;
  status?: ShipmentStatus; // Ensure the status matches one of the defined statuses
}

// Map status keys to their display text
// const STATUS_TEXTS = {
//   DELIVERED: 'delivered',
//   ON_HOLD: 'on_hold',
//   IN_PROGRESS: 'in_progress',
//   CREATED: 'created',
// };

// Update the component to accept props
const OrderInfoCard: React.FC<OrderInfoCardProps> = ({
  title,
  orderIdLabel,
  orderId,
  estimateDeliveryLabel,
  estimateDelivery,
  trackingUrlLabel,
  trackingUrl,
  trackingAuth,
  clueVerificationLink,
  idCheck,
  status,
}) => {
  const { t } = useTranslation();

  const handleCopy = (text?: string) => {
    if (text) {
      navigator.clipboard.writeText(text);
      message.success(t('copy'));
    }
  };

  return (
    <Card extraClassName='!shadow-card-dark mb-4'>
      <Flex justify='space-between'>
        <Heading
          title={title}
          fontSize='text-lg'
          fontWeight='font-bold'
          extraClassName='uppercase'
        />
        <StatusTag
          text={status?.slug ? t(`merchant_title_order_status.${status.slug}`) : ''}
          color={getOrderStatusTagData(status?.slug as OrderStatusEnum)?.tagColor || 'processing'}
          iconColor={getOrderStatusTagData(status?.slug as OrderStatusEnum)?.iconColor || ''}
        />
      </Flex>
      <Flex vertical gap={8} className='mt-4'>
        <Flex justify='space-between' className='w-full'>
          <Flex vertical>
            <Text className=' font-normal text-sm leading-[18.2px] text-customGrayShade5'>
              {orderIdLabel}
            </Text>
            <Text className=' font-normal text-base leading-[20.48px] mt-1 text-customTunaPrimary'>
              {orderId}
            </Text>
          </Flex>
          <Flex vertical className='w-24'>
            <Text className=' font-normal text-sm leading-[18.2px] text-customGrayShade5'>
              {trackingUrlLabel}
            </Text>
            <Flex gap={11} className='mt-1'>
              <CopyIcon onClick={() => handleCopy(trackingAuth)} className='cursor-pointer' />
              <a href={trackingUrl} target='_blank' rel='noopener noreferrer'>
                <UrlIcon />
              </a>
            </Flex>
          </Flex>
        </Flex>
        {clueVerificationLink && (
          <Flex justify='space-between' className='w-full'>
            <Flex vertical>
              <Text className=' font-normal text-sm leading-[18.2px] text-customGrayShade5'>
                {t('clue_verification')}
              </Text>
              <Text className=' font-normal text-base leading-[20.48px] mt-1 text-customTunaPrimary'>
                {clueVerificationLink}
              </Text>
            </Flex>
            <Flex vertical className='w-24'>
              <Text className=' font-normal text-sm leading-[18.2px] text-customGrayShade5'>
                {t('clue_url')}
              </Text>
              <Flex gap={11} className='mt-1'>
                <CopyIcon
                  onClick={() => handleCopy(clueVerificationLink)}
                  className='cursor-pointer'
                />
                <a href={clueVerificationLink} target='_blank' rel='noopener noreferrer'>
                  <UrlIcon />
                </a>
              </Flex>
            </Flex>
          </Flex>
        )}
        {idCheck && (
          <Flex justify='space-start' className='w-full'>
            <Flex vertical>
              <Text className=' font-normal text-sm leading-[18.2px] text-customGrayShade5'>
                {t('id_checked')}
              </Text>
              <Text className=' font-normal text-base leading-[20.48px] mt-1 text-customTunaPrimary'>
                {idCheck ? t('yes') : t('no')}
              </Text>
            </Flex>
          </Flex>
        )}
      </Flex>
      {/* <Col lg={7} md={24} xs={24}>
        <Flex vertical>
            <Text className=' font-normal text-sm leading-[18.2px] text-customGrayShade5'>
              {estimateDeliveryLabel}
            </Text>
            <Text className=' font-normal text-base leading-[20.48px] mt-1 text-customTunaPrimary'>
              {estimateDelivery}
            </Text>
          </Flex>
        </Col> */}
    </Card>
  );
};

export default OrderInfoCard;
